/**
 * The one time-weighted return chain behind Gain, Growth, Drawdown, Monthly and
 * Yearly.
 *
 * These five numbers were computed by three near-identical loops (the chart
 * tabs, the You tile, and the trailing-period series), which is how they drifted
 * apart before. They now share this one.
 *
 * ## Why a sub-floor day is a gap, not a -100% day
 *
 * The chain is multiplicative, so zero is absorbing: once `index` hits ~0 it can
 * never recover, and every downstream figure reads -100% forever. Brokers emit
 * the occasional junk NAV row — a 0.00 or a few cents on a day they had no
 * valuation — and one of those is enough to destroy the entire history.
 *
 * The old guard (`prev >= floor`) made this strictly worse rather than better,
 * because it was asymmetric. Going *into* the junk day the base was healthy, so
 * the -99.99% step was applied. Coming *out* of it the base was the junk value,
 * which is below the floor, so the +2,000,000% recovery step was skipped. The
 * guard let the fall through and blocked the rebound.
 *
 * A production Alpaca account showed exactly this: Gain -100.00%, Yearly
 * -100.00% and Drawdown -100.00%, sitting beside a Profit of +$2,216.17 and an
 * equity of $32,216.17 that were both perfectly correct. Profit survived because
 * it is additive, so the false drop and the false rebound canceled out.
 *
 * So: a reading below the materiality floor is treated as a hole in the series.
 * The step into it is not applied and the base is not advanced, so measurement
 * resumes from the last real equity when the data comes back.
 *
 * ## The exception that keeps it honest
 *
 * An account that genuinely goes to zero must still report the loss. The tell is
 * whether the money ever comes back: a sub-floor reading with measurable equity
 * later is bad data, while one with nothing measurable after it is a real
 * wipeout. Only the latter is applied.
 */

import { materialityFloor, preparePortfolioSeries } from './clientReportingInception';

/**
 * @param {Array<{date: string, value: number, netFlow: number}>} rawPoints
 * @returns {{
 *   points: Array<{date, index, growthPct, drawdownPct, cumProfit}>,
 *   totalReturnPct: number,
 *   maxDrawdownPct: number,
 *   floor: number,
 *   gapDates: string[],
 * }}
 */
export function chainReturns(rawPoints) {
  const empty = {
    points: [], totalReturnPct: 0, maxDrawdownPct: 0, floor: 0, gapDates: [],
  };
  if (!rawPoints?.length) return empty;

  // Infer missing Alpaca deposits, then start where the money did.
  const points = preparePortfolioSeries(rawPoints);
  if (!points.length) return empty;

  const floor = materialityFloor(points);

  // The last day the account held a measurable amount. A sub-floor reading
  // before this is a hole (equity returns afterwards); one after it is the tail
  // of a real collapse.
  let lastMeasurable = -1;
  for (let i = points.length - 1; i >= 0; i -= 1) {
    if ((Number(points[i].value) || 0) >= floor) { lastMeasurable = i; break; }
  }

  let index = 1;
  let peak = 1;
  let maxDrawdown = 0;
  let cumProfit = 0;
  // The last equity worth measuring against. It does NOT advance across a gap,
  // which is what lets the next real reading be compared to the last real one.
  let base = Number(points[0].value) || 0;
  // Deposits Alpaca (and others) stamp on day T against a NAV that is still
  // yesterday's. Applied on T they look like a -100% loss; applied on T+1,
  // when the cash actually appears, they are just funding. Carry them forward
  // across a gap so they land on the day the balance moves.
  let pendingFlow = 0;
  const gapDates = [];

  const out = [{
    date: points[0].date, index, growthPct: 0, drawdownPct: 0, cumProfit: 0,
  }];

  for (let i = 1; i < points.length; i += 1) {
    const point = points[i];
    const value = Number(point.value) || 0;
    const rawFlow = Number(point.netFlow) || 0;
    const flow = rawFlow + pendingFlow;

    const isWipeoutTail = i > lastMeasurable;
    const r = base >= floor ? Math.max((value - flow - base) / base, -1) : null;
    // A -100% step while the account still holds a material balance is
    // unsettled cash, not a wipeout. Production Alpaca: 2026-06-25 equity
    // $2,000 with a $28,000 deposit; the $30,000 NAV arrived on the 26th.
    const fakeWipeout = !isWipeoutTail && r !== null && r <= -1 && value >= floor;
    const usable = (value >= floor && !fakeWipeout) || isWipeoutTail;

    if (!usable) {
      // A hole: hold the base so the next real reading is measured against the
      // last real equity rather than against junk. Carry the day's flow with
      // us — it belongs to the day the cash actually appears in NAV.
      //
      // Profit is held across the gap too. It is additive, so leaving it on the
      // raw row-to-row difference still telescopes to the right total — but the
      // Profit chart would cliff to -$30,000 for one day and climb back, which
      // is the broker's bad reading rendered as a real loss.
      gapDates.push(point.date);
      pendingFlow += rawFlow;
    } else {
      pendingFlow = 0;
      cumProfit += value - base - flow;
      if (base >= floor && r !== null) {
        index *= 1 + r;
        if (index > peak) peak = index;
        const dd = peak > 0 ? (index - peak) / peak : 0;
        if (dd < maxDrawdown) maxDrawdown = dd;
      }
      // Re-base even when the step was unmeasurable, so an account that opens
      // below the floor still starts being measured once it is funded.
      base = value;
    }

    out.push({
      date: point.date,
      index,
      growthPct: (index - 1) * 100,
      drawdownPct: peak > 0 ? ((index - peak) / peak) * 100 : 0,
      cumProfit: Math.round(cumProfit * 1e6) / 1e6,
    });
  }

  return {
    points: out,
    totalReturnPct: (index - 1) * 100,
    maxDrawdownPct: maxDrawdown * 100,
    floor,
    gapDates,
  };
}

/**
 * Net flows applied on each date, using the same gap-carry rules as chainReturns().
 *
 * Deposits Alpaca stamps before NAV updates land on the day measurement resumes,
 * so benchmark overlays can step up when capital actually appears in equity.
 *
 * @returns {Map<string, number>}
 */
export function effectiveFlowsByDate(rawPoints) {
  const points = preparePortfolioSeries(rawPoints);
  if (points.length < 2) return new Map();

  const floor = materialityFloor(points);
  let lastMeasurable = -1;
  for (let i = points.length - 1; i >= 0; i -= 1) {
    if ((Number(points[i].value) || 0) >= floor) { lastMeasurable = i; break; }
  }

  const flows = new Map();
  let base = Number(points[0].value) || 0;
  let pendingFlow = 0;

  for (let i = 1; i < points.length; i += 1) {
    const point = points[i];
    const value = Number(point.value) || 0;
    const rawFlow = Number(point.netFlow) || 0;
    const flow = rawFlow + pendingFlow;

    const isWipeoutTail = i > lastMeasurable;
    const r = base >= floor ? Math.max((value - flow - base) / base, -1) : null;
    const fakeWipeout = !isWipeoutTail && r !== null && r <= -1 && value >= floor;
    const usable = (value >= floor && !fakeWipeout) || isWipeoutTail;

    if (!usable) {
      pendingFlow += rawFlow;
    } else {
      pendingFlow = 0;
      if (flow !== 0) {
        flows.set(point.date, (flows.get(point.date) || 0) + flow);
      }
      base = value;
    }
  }

  return flows;
}
